import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, Trash2, Eye, EyeOff } from 'lucide-react';
import { ACCESS_TYPES, ACCESS_TYPE_LABELS, type CreateProductInput } from '@aggregator/shared';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Field } from '@/components/ui/label';
import { Dialog } from '@/components/ui/dialog';
import { Table, THead, TBody, TR, TH, TD, EmptyRow } from '@/components/ui/table';
import { PageHeader, Loading, ErrorState, Pagination } from '@/components/ui/states';
import { useToast } from '@/components/ui/toast';
import { useProducts, useCreateProduct, useDeleteProduct, useUpdateProduct, useSuppliers } from '@/api/hooks';
import type { AdminProductListItem } from '@/api/types';
import { errorMessage } from '@/api/client';
import { formatUsdt } from '@/lib/utils';

const emptyProduct: CreateProductInput = {
  name: '',
  durationDays: 30,
  accessType: 'unknown',
  region: 'global',
  warrantyDays: 0,
  category: 'uncategorised',
  tags: [],
  description: '',
  isActive: true,
  imageUrl: '',
};

export function ProductsPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const suppliers = useSuppliers();
  const products = useProducts({ page, pageSize: 15, search: debounced || undefined, supplierId: supplierId || undefined });
  const create = useCreateProduct();
  const { toast } = useToast();

  const [addOpen, setAddOpen] = useState(false);
  const [draft, setDraft] = useState<CreateProductInput>(emptyProduct);
  const [tagsText, setTagsText] = useState('');

  function submit() {
    create.mutate(
      { ...draft, tags: tagsText.split(',').map((t) => t.trim()).filter(Boolean) },
      {
        onSuccess: () => {
          toast({ title: 'Product created', variant: 'success' });
          setAddOpen(false);
          setDraft(emptyProduct);
          setTagsText('');
        },
        onError: (e) => toast({ title: 'Could not create', description: errorMessage(e), variant: 'error' }),
      },
    );
  }

  return (
    <div>
      <PageHeader
        title="Products"
        description="Normalised products aggregated across suppliers."
        actions={
          <Button onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4" /> Add Product
          </Button>
        }
      />

      <div className="mb-4 flex items-center gap-2">
        <div className="relative max-w-xs flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-8"
            placeholder="Search products…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setPage(1);
                setDebounced(search);
              }
            }}
          />
        </div>
        <Select
          value={supplierId}
          onChange={(e) => { setSupplierId(e.target.value); setPage(1); }}
          options={[
            { value: '', label: 'All suppliers' },
            ...(suppliers.data ?? []).map((supplier) => ({ value: supplier.id, label: supplier.name })),
          ]}
          className="max-w-xs"
        />
        <Button
          variant="outline"
          onClick={() => {
            setPage(1);
            setDebounced(search);
          }}
        >
          Search
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {products.isLoading ? (
            <Loading />
          ) : products.isError ? (
            <ErrorState error={products.error} onRetry={() => products.refetch()} />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Name</TH>
                  <TH>Category</TH>
                  <TH>Price</TH>
                  <TH>Stock</TH>
                  <TH>Suppliers</TH>
                  <TH>Status</TH>
                  <TH />
                </TR>
              </THead>
              <TBody>
                {products.data && products.data.items.length > 0 ? (
                  products.data.items.map((p) => {
                    const cheapest = p.rows.find((r) => r.isCheapest);
                    const inStock = p.rows.some((r) => r.inStock);
                    return (
                      <TR key={p.id}>
                        <TD>
                          <div className="flex items-center gap-2">
                            {p.imageUrl ? (
                              <img src={p.imageUrl} alt="" className="h-6 w-6 rounded object-cover" />
                            ) : null}
                            <Link className="font-medium hover:underline" to={`/products/${p.id}`}>
                              {p.name}
                            </Link>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {p.durationDays}d · {p.region}
                            {p.pricingOverride != null ? ' · manual price' : ''}
                          </div>
                        </TD>
                        <TD className="text-muted-foreground">{p.category}</TD>
                        <TD>
                          {cheapest ? `${formatUsdt(cheapest.sellingPriceUsdt)} USDT` : p.pricingOverride != null ? `${formatUsdt(p.pricingOverride)} USDT` : '—'}
                        </TD>
                        <TD>
                          <Badge variant={inStock ? 'success' : 'secondary'}>{inStock ? `${p.stockCount} available` : 'Out'}</Badge>
                        </TD>
                        <TD>{p.supplierCount}</TD>
                        <TD>
                          <Badge variant={p.isActive ? 'default' : 'secondary'}>
                            {p.isActive ? 'Active' : 'Hidden'}
                          </Badge>
                        </TD>
                        <TD className="text-right">
                          <ProductRowActions product={p} />
                        </TD>
                      </TR>
                    );
                  })
                ) : (
                  <EmptyRow colSpan={7}>No products. Run a supplier sync or add one manually.</EmptyRow>
                )}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {products.data ? (
        <Pagination page={products.data.page} totalPages={products.data.totalPages} onPage={setPage} />
      ) : null}

      <Dialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title="Add product"
        description="Metadata only — attach suppliers via sync or the product page."
        footer={
          <>
            <Button variant="outline" onClick={() => setAddOpen(false)}>
              Cancel
            </Button>
            <Button loading={create.isPending} onClick={submit}>
              Create
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Name">
            <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Duration (days)">
              <Input
                type="number"
                value={draft.durationDays}
                onChange={(e) => setDraft({ ...draft, durationDays: Number(e.target.value) || 0 })}
              />
            </Field>
            <Field label="Warranty (days)">
              <Input
                type="number"
                value={draft.warrantyDays}
                onChange={(e) => setDraft({ ...draft, warrantyDays: Number(e.target.value) || 0 })}
              />
            </Field>
            <Field label="Region">
              <Input value={draft.region} onChange={(e) => setDraft({ ...draft, region: e.target.value })} />
            </Field>
            <Field label="Access type">
              <Select
                value={draft.accessType}
                onChange={(e) => setDraft({ ...draft, accessType: e.target.value as CreateProductInput['accessType'] })}
                options={ACCESS_TYPES.map((a) => ({ value: a, label: ACCESS_TYPE_LABELS[a] }))}
              />
            </Field>
          </div>
          <Field label="Category">
            <Input value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} />
          </Field>
          <Field label="Tags (comma separated)">
            <Input value={tagsText} onChange={(e) => setTagsText(e.target.value)} placeholder="streaming, edu" />
          </Field>
          <Field label="Description">
            <Input value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
          </Field>
          <Field label="Icon / image URL" hint="Shown on the product's card in the bot, e.g. the service's logo.">
            <Input
              value={draft.imageUrl}
              onChange={(e) => setDraft({ ...draft, imageUrl: e.target.value })}
              placeholder="https://…/icon.png"
            />
          </Field>
        </div>
      </Dialog>
    </div>
  );
}

function ProductRowActions({ product }: { product: AdminProductListItem }) {
  const update = useUpdateProduct(product.id);
  const del = useDeleteProduct();
  const { toast } = useToast();

  return (
    <div className="flex justify-end gap-1">
      <Button
        variant="ghost"
        size="sm"
        title={product.isActive ? 'Deactivate (hide from the bot)' : 'Activate (show in the bot)'}
        loading={update.isPending}
        onClick={() =>
          update.mutate(
            { isActive: !product.isActive },
            {
              onSuccess: () => toast({ title: product.isActive ? 'Product deactivated' : 'Product activated', variant: 'success' }),
              onError: (e) => toast({ title: 'Update failed', description: errorMessage(e), variant: 'error' }),
            },
          )
        }
      >
        {product.isActive ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        {product.isActive ? 'Deactivate' : 'Activate'}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        title="Delete permanently"
        loading={del.isPending}
        onClick={() => {
          if (!confirm(`Delete "${product.name}" permanently? This cannot be undone.`)) return;
          del.mutate(product.id, {
            onSuccess: () => toast({ title: 'Product deleted', variant: 'success' }),
            onError: (e) => toast({ title: 'Delete failed', description: errorMessage(e), variant: 'error' }),
          });
        }}
      >
        <Trash2 className="h-4 w-4 text-destructive" />
        Delete
      </Button>
    </div>
  );
}
